/**
 * DangerZone — venue lifecycle end-states (port of the design's NBDangerZone).
 * "Mark as dropped" requires a reason (kept on the record + audit log) and
 * patches stage → dropped. Hard delete stays disabled while any workshop
 * references the venue; the backend enforces the zero-workshop guard too.
 *
 * The red card shell + row layout come from the shared {@link DangerZonePanel}
 * primitives (BON-202); only the venue-specific rows live here.
 */
import { useState } from 'react';

import { DANGER_RED, DangerZonePanel, DangerZoneRow } from '../../primitives';

export interface DangerZoneProps {
  /** Why permanent delete is unavailable, or null when it's allowed. */
  deleteBlockedReason: string | null;
  onDrop: (reason: string) => Promise<void>;
  onDelete: () => Promise<void>;
}

function DropRow({ onDrop }: { onDrop: (reason: string) => Promise<void> }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const disabled = busy || reason.trim().length === 0;

  const drop = async () => {
    if (disabled) return;
    setBusy(true);
    try {
      await onDrop(reason.trim());
      setReason('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <DangerZoneRow
      title="Mark as dropped"
      note="Deliberate decision to stop hosting here. Requires a reason — kept on the record and in the audit log."
    >
      <input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason (required)"
        style={{
          width: 220,
          height: 32,
          padding: '0 10px',
          borderRadius: 8,
          fontSize: 12.5,
          background: 'var(--nb-bg)',
          color: 'var(--nb-ink)',
          border: '1px solid var(--nb-border-2)',
          outline: 'none',
        }}
      />
      <button
        type="button"
        className="nb-btn"
        onClick={() => void drop()}
        disabled={disabled}
        style={{
          color: DANGER_RED,
          borderColor: 'rgba(192,57,43,.4)',
          whiteSpace: 'nowrap',
          opacity: disabled ? 0.45 : 1,
        }}
      >
        Mark as dropped
      </button>
    </DangerZoneRow>
  );
}

function DeleteRow({
  deleteBlockedReason,
  onDelete,
}: {
  deleteBlockedReason: string | null;
  onDelete: () => Promise<void>;
}) {
  const canDelete = deleteBlockedReason === null;
  return (
    <DangerZoneRow
      title="Delete permanently"
      warn={!canDelete}
      divided
      note={
        deleteBlockedReason ??
        'Removes the venue entirely. Only available once no workshops reference it.'
      }
    >
      <button
        type="button"
        className="nb-btn"
        disabled={!canDelete}
        onClick={() => void onDelete()}
        style={{
          color: DANGER_RED,
          borderColor: 'rgba(192,57,43,.4)',
          opacity: canDelete ? 1 : 0.45,
          cursor: canDelete ? 'pointer' : 'not-allowed',
        }}
      >
        Delete
      </button>
    </DangerZoneRow>
  );
}

export function DangerZone({ deleteBlockedReason, onDrop, onDelete }: DangerZoneProps) {
  return (
    <DangerZonePanel>
      <DropRow onDrop={onDrop} />
      <DeleteRow deleteBlockedReason={deleteBlockedReason} onDelete={onDelete} />
    </DangerZonePanel>
  );
}
